import React, { useState, useEffect, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Bot, User, Copy, Check } from 'lucide-react'
import { useVoice } from '../contexts/VoiceContext'
import { useTransaction } from '../contexts/TransactionContext'
import { useWeb3Wallet } from '../contexts/RainbowKitProvider'
import { parseVoiceCommand, generateResponse } from '../services/nlpAgent'
import { llmService } from '../services/llmService'

interface Message {
  id: string
  type: 'user' | 'assistant'
  content: string
  timestamp: number
  command?: any
  isProcessing?: boolean
}

interface AssistantChatProps {
  className?: string
}

const AssistantChat: React.FC<AssistantChatProps> = ({ className = '' }) => {
  const [messages, setMessages] = useState<Message[]>([])
  const [copiedId, setCopiedId] = useState<string | null>(null)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const { speak, language } = useVoice()
  const { addTransaction } = useTransaction()
  const { isConnected, balance, tokenBalances } = useWeb3Wallet()

  // Auto-scroll to bottom only for new messages, not on mount
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  // Only scroll when new messages are added (not on initial load)
  useEffect(() => {
    if (messages.length > 1) { // Only scroll if there's more than welcome message
      scrollToBottom()
    }
  }, [messages.length])

  // Add welcome message on mount (without auto-speaking)
  useEffect(() => {
    const welcomeMessage: Message = {
      id: 'welcome',
      type: 'assistant',
      content: getWelcomeMessage(language),
      timestamp: Date.now()
    }
    setMessages([welcomeMessage])
    
    // Only speak welcome message when app is first opened (not on every reload)
    const hasSpokenWelcome = sessionStorage.getItem('hasSpokenWelcome')
    const isFirstVisit = !localStorage.getItem('hasVisitedBefore')
    
    if (isFirstVisit && !hasSpokenWelcome) {
      setTimeout(() => {
        speak(welcomeMessage.content, language)
        sessionStorage.setItem('hasSpokenWelcome', 'true')
        localStorage.setItem('hasVisitedBefore', 'true')
      }, 1000)
    }
  }, [language, speak])

  const getWelcomeMessage = (lang: string): string => {
    const messages: Record<string, string> = {
      'en-US': 'Hello! I\'m your voice payment assistant. You can ask me to send payments, check balances, or view transaction history. Just speak naturally!',
      'es-ES': '¡Hola! Soy tu asistente de pagos por voz. Puedes pedirme que envíe pagos, revise saldos o vea el historial de transacciones. ¡Solo habla naturalmente!',
      'fr-FR': 'Bonjour! Je suis votre assistant de paiement vocal. Vous pouvez me demander d\'envoyer des paiements, de vérifier les soldes ou de voir l\'historique des transactions. Parlez naturellement!',
      'de-DE': 'Hallo! Ich bin Ihr Sprach-Zahlungsassistent. Sie können mich bitten, Zahlungen zu senden, Guthaben zu überprüfen oder Transaktionsverläufe anzuzeigen. Sprechen Sie einfach natürlich!'
    }
    
    const langCode = lang.split('-')[0]
    return messages[lang] || messages[`${langCode}-${langCode.toUpperCase()}`] || messages['en-US']
  }

  const addMessage = (content: string, type: 'user' | 'assistant', command?: any) => {
    const message: Message = {
      id: Date.now().toString(),
      type,
      content,
      timestamp: Date.now(),
      command
    }
    
    setMessages(prev => [...prev, message])
    return message.id
  }

  const updateMessage = (id: string, updates: Partial<Message>) => {
    setMessages(prev => prev.map(msg => 
      msg.id === id ? { ...msg, ...updates } : msg
    ))
  }

  const handleUserInput = async (transcript: string) => {
    if (!transcript.trim()) return

    // Add user message
    addMessage(transcript, 'user')
    
    // Add processing message
    const processingMessageId = addMessage('Processing your request...', 'assistant')
    updateMessage(processingMessageId, { isProcessing: true })

    try {
      // Use LLM service for enhanced AI response
      const llmResult = await llmService.processVoiceCommand(transcript, language)
      
      // Fallback to original NLP if LLM fails
      let command = null
      let response = llmResult.response
      
      if (llmResult.action && llmResult.parameters) {
        command = {
          action: llmResult.action,
          confidence: 0.9,
          ...llmResult.parameters
        }
      } else {
        // Fallback to original parsing
        command = await parseVoiceCommand(transcript, language)
        if (!response || response === llmResult.response) {
          response = generateResponse(command, language)
        }
      }
      
      // Handle balance check with real-time data
      const isBalanceCommand = command?.action === 'balance' || 
                              llmResult.action === 'check' || 
                              llmResult.action === 'balance' ||
                              transcript.toLowerCase().includes('balance') ||
                              transcript.toLowerCase().includes('saldo') ||
                              transcript.toLowerCase().includes('check')
      
      if (isBalanceCommand) {
        try {
          if (isConnected) {
            const bnbBalance = parseFloat(balance || '0').toFixed(4)
            const voiceBalance = tokenBalances?.find((token: any) => token.symbol === 'VOICE')?.balance || '0'
            
            response = `Your current balances:\n• BNB: ${bnbBalance} BNB\n• VOICE: ${parseFloat(voiceBalance).toFixed(2)} VOICE`
          } else {
            response = 'Please connect your wallet first to check your balance.'
          }
        } catch (balanceError) {
          console.error('Balance check error:', balanceError)
          response = 'Unable to retrieve balance at the moment. Please try again.'
        }
      }
      
      // Update processing message with actual response
      updateMessage(processingMessageId, {
        content: response,
        command,
        isProcessing: false
      })

      // Speak response
      speak(response, language)

      // Handle specific actions
      if (command && command.action === 'send' && (command.confidence > 0.7 || llmResult.action === 'send')) {
        // Add transaction to pending
        addTransaction({
          type: 'send',
          blockchain: command.blockchain || 'bnb',
          token: command.token || 'BNB',
          amount: command.amount || '0',
          recipient: command.recipient,
          reason: command.reason,
          status: 'pending',
          voiceCommand: transcript
        })
      }
    } catch (error) {
      console.error('Failed to process voice command:', error)
      updateMessage(processingMessageId, {
        content: 'Sorry, I couldn\'t process that request. Please try again.',
        isProcessing: false
      })
    }
  }

  const copyToClipboard = async (text: string, messageId: string) => {
    try {
      await navigator.clipboard.writeText(text)
      setCopiedId(messageId)
      setTimeout(() => setCopiedId(null), 2000)
    } catch (error) {
      console.error('Failed to copy text:', error)
    }
  }

  const formatTimestamp = (timestamp: number): string => {
    return new Date(timestamp).toLocaleTimeString([], { 
      hour: '2-digit', 
      minute: '2-digit' 
    })
  }

  const getQuickCommands = (): string[] => {
    const commands: Record<string, string[]> = {
      'en-US': [
        'Check my balance',
        'Send 0.1 BNB',
        'Show transaction history',
        'What can you do?'
      ],
      'es-ES': [
        'Revisar mi saldo',
        'Enviar 0.1 BNB',
        'Mostrar historial',
        '¿Qué puedes hacer?'
      ],
      'fr-FR': [
        'Vérifier mon solde',
        'Envoyer 0.1 BNB',
        'Afficher l\'historique',
        'Que pouvez-vous faire?'
      ]
    }
    
    const langCode = language.split('-')[0]
    return commands[language] || commands[`${langCode}-${langCode.toUpperCase()}`] || commands['en-US']
  }

  return (
    <div className={`flex flex-col h-full ${className}`}>
      {/* Messages Container */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4 scrollbar-thin scrollbar-thumb-white/20 scrollbar-track-transparent">
        <AnimatePresence initial={false}>
          {messages.map((message) => (
            <motion.div
              key={message.id}
              initial={{ opacity: 0, y: 20, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -20, scale: 0.95 }}
              transition={{ duration: 0.3 }}
              className={`flex items-start space-x-3 ${
                message.type === 'user' ? 'flex-row-reverse space-x-reverse' : ''
              }`}
            >
              {/* Avatar */}
              <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                message.type === 'user' 
                  ? 'bg-blue-500/20 border border-blue-400/30' 
                  : 'bg-purple-500/20 border border-purple-400/30'
              }`}>
                {message.type === 'user' ? (
                  <User className="w-4 h-4 text-blue-300" />
                ) : (
                  <Bot className="w-4 h-4 text-purple-300" />
                )}
              </div>

              {/* Message Content */}
              <div className={`flex-1 max-w-xs sm:max-w-md ${
                message.type === 'user' ? 'text-right' : 'text-left'
              }`}>
                <div className={`relative p-3 rounded-2xl glass border border-white/20 ${
                  message.type === 'user'
                    ? 'bg-blue-500/10 border-blue-400/30'
                    : 'bg-white/5 border-white/20'
                }`}>
                  {message.isProcessing ? (
                    <div className="flex items-center space-x-2">
                      <div className="flex space-x-1">
                        {[...Array(3)].map((_, i) => (
                          <motion.div
                            key={`processing-dot-${message.id}-${i}`}
                            className="w-2 h-2 bg-purple-400 rounded-full"
                            animate={{ scale: [1, 1.2, 1] }}
                            transition={{
                              duration: 1,
                              repeat: Infinity,
                              delay: i * 0.2
                            }}
                          />
                        ))}
                      </div>
                      <span className="text-white/70 text-sm">{message.content}</span>
                    </div>
                  ) : (
                    <p className="text-white text-sm leading-relaxed">
                      {message.content}
                    </p>
                  )}

                  {/* Copy Button */}
                  {!message.isProcessing && (
                    <button
                      onClick={() => copyToClipboard(message.content, message.id)}
                      className="absolute top-1 right-1 p-1 rounded opacity-0 group-hover:opacity-100 hover:bg-white/10 transition-all duration-200"
                    >
                      {copiedId === message.id ? (
                        <Check className="w-3 h-3 text-green-400" />
                      ) : (
                        <Copy className="w-3 h-3 text-white/50" />
                      )}
                    </button>
                  )}
                </div>

                {/* Timestamp */}
                <p className={`text-xs text-white/40 mt-1 ${
                  message.type === 'user' ? 'text-right' : 'text-left'
                }`}>
                  {formatTimestamp(message.timestamp)}
                </p>

                {/* Command Info */}
                {message.command && message.command.confidence > 0.5 && (
                  <div className={`mt-2 text-xs ${
                    message.type === 'user' ? 'text-right' : 'text-left'
                  }`}>
                    <span className="inline-flex items-center px-2 py-1 rounded-full bg-white/10 text-white/60">
                      {message.command.action} • {Math.round(message.command.confidence * 100)}% confidence
                    </span>
                  </div>
                )}
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
        <div ref={messagesEndRef} />
      </div>

      {/* Quick Commands */}
      <div className="p-4 border-t border-white/10">
        <p className="text-xs text-white/60 mb-2">Quick commands:</p>
        <div className="flex flex-wrap gap-2">
          {getQuickCommands().map((command, index) => (
            <button
              key={`quick-command-${index}-${command.replace(/\s+/g, '-').toLowerCase()}`}
              onClick={() => handleUserInput(command)}
              className="px-3 py-1 text-xs glass rounded-full border border-white/20 text-white/80 hover:bg-white/10 transition-all duration-200"
            >
              {command}
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}

export default AssistantChat